import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { WeeklyPlan, WeeklyGoal, DayTheme } from '@/types';

// ============================================
// Collection Path Helpers
// ============================================

const getWeeklyPlanRef = (userId: string, weekId: string) =>
  doc(db, 'users', userId, 'weeklyPlans', weekId);

// ============================================
// Helper Functions
// ============================================

/**
 * Get the start (Monday) and end (Sunday) dates for a given week ID
 */
export function getWeekBounds(weekId: string): { start: string; end: string } {
  const match = weekId.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid week ID format: ${weekId}. Expected YYYY-Www`);
  }

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

  const weekMonday = new Date(week1Monday);
  weekMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);

  const weekSunday = new Date(weekMonday);
  weekSunday.setUTCDate(weekMonday.getUTCDate() + 6);

  const formatDate = (d: Date): string => {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    start: formatDate(weekMonday),
    end: formatDate(weekSunday),
  };
}

/**
 * Get all dates in a week as YYYY-MM-DD strings
 */
export function getWeekDates(weekId: string): string[] {
  const { start } = getWeekBounds(weekId);
  const startDate = new Date(start + 'T00:00:00Z');
  const dates: string[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setUTCDate(startDate.getUTCDate() + i);
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    dates.push(`${yyyy}-${mm}-${dd}`);
  }

  return dates;
}

function createDefaultWeeklyPlan(userId: string, weekId: string): Omit<WeeklyPlan, 'id'> {
  const { start, end } = getWeekBounds(weekId);
  const dates = getWeekDates(weekId);

  const dayThemes: Record<string, DayTheme> = {};
  for (const date of dates) {
    dayThemes[date] = null;
  }

  return {
    userId,
    weekStart: start,
    weekEnd: end,
    goals: [],
    dayThemes,
    notes: '',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
}

// ============================================
// CRUD Operations
// ============================================

export async function getWeeklyPlan(userId: string, weekId: string): Promise<WeeklyPlan | null> {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);
  const snapshot = await getDoc(weekPlanRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  return {
    id: snapshot.id,
    userId: data.userId,
    weekStart: data.weekStart,
    weekEnd: data.weekEnd,
    goals: data.goals || [],
    dayThemes: data.dayThemes || {},
    notes: data.notes || '',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export async function createWeeklyPlan(
  userId: string,
  weekId: string,
  data?: Partial<Omit<WeeklyPlan, 'id'>>
): Promise<WeeklyPlan> {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);
  const defaultPlan = createDefaultWeeklyPlan(userId, weekId);
  const newPlan = { ...defaultPlan, ...data };

  await setDoc(weekPlanRef, newPlan);

  return {
    id: weekId,
    ...newPlan,
  };
}

export async function updateWeeklyPlan(
  userId: string,
  weekId: string,
  updates: Partial<WeeklyPlan>
): Promise<void> {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);
  await updateDoc(weekPlanRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function updateWeeklyPlanGoals(
  userId: string,
  weekId: string,
  goals: WeeklyGoal[]
): Promise<void> {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);
  await updateDoc(weekPlanRef, {
    goals,
    updatedAt: serverTimestamp(),
  });
}

export async function updateWeeklyPlanDayTheme(
  userId: string,
  weekId: string,
  dayThemes: Record<string, DayTheme>
): Promise<void> {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);
  await updateDoc(weekPlanRef, {
    dayThemes,
    updatedAt: serverTimestamp(),
  });
}

export async function updateWeeklyPlanNotes(
  userId: string,
  weekId: string,
  notes: string
): Promise<void> {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);
  await updateDoc(weekPlanRef, {
    notes,
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// Subscriptions
// ============================================

export function subscribeToWeeklyPlan(
  userId: string,
  weekId: string,
  onData: (plan: WeeklyPlan | null) => void,
  onError?: (error: Error) => void
): () => void {
  const weekPlanRef = getWeeklyPlanRef(userId, weekId);

  return onSnapshot(
    weekPlanRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const plan: WeeklyPlan = {
          id: snapshot.id,
          userId: data.userId,
          weekStart: data.weekStart,
          weekEnd: data.weekEnd,
          goals: data.goals || [],
          dayThemes: data.dayThemes || {},
          notes: data.notes || '',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        };
        onData(plan);
      } else {
        onData(null);
      }
    },
    (error) => {
      console.error('Error listening to weekly plan:', error);
      onError?.(error);
    }
  );
}
